import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* eslint-disable */
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("edge_token");
    if (!token) { setLoading(false); return; }
    (async () => {
      // Retry /auth/me up to 3 times before clearing the token. A single
      // 401 or network error during initial page load (backend cold-start,
      // Cloudflare 502, transient DNS blip) used to log users out — that
      // NEVER happens now. Only if 3 consecutive attempts fail with a 401
      // do we treat the token as truly invalid.
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const r = await api.get("/auth/me");
          if (!cancelled) {
            setUser(r.data);
            // Seed the "last-good billing" cache from the user record. This
            // guards against the "kicked to /pricing on refresh" bug for a
            // customer who has a valid subscribed_until on the server but
            // whose localStorage cache was cleared (browser wipe, new device,
            // incognito, cache-clearing extension, etc.).
            try {
              const sub = r.data?.subscribed_until;
              const trial = r.data?.trial_ends_at;
              const hasSub = sub && new Date(sub).getTime() > Date.now();
              const inTrial = trial && new Date(trial).getTime() > Date.now();
              if (hasSub || inTrial) {
                localStorage.setItem("edge_last_good_billing", JSON.stringify({
                  at: Date.now(),
                  has_access: true,
                }));
              }
            } catch { /* localStorage blocked — ignore */ }
          }
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          // Non-401 error (5xx, network, timeout) — retry with backoff but
          // DON'T touch the token. We'll pretend the user is logged in
          // (from the token they have) and the app will re-verify on next
          // load. This is safer than kicking them out on a transient error.
          if (status !== 401) {
            if (attempt < 2) {
              await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }
            // After 3 failures still no 401 — leave user null but keep token.
            // Next page load will try again.
            break;
          }
          // 401 — but wait, could be a transient auth blip. Retry once more
          // before declaring the token dead.
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          }
        }
      }
      // Only clear the token if we got THREE consecutive 401s (not just any
      // failure). Any 5xx / network error leaves the token alone.
      if (lastErr && lastErr?.response?.status === 401) {
        localStorage.removeItem("edge_token");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);
  /* eslint-enable */

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("edge_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (email, password, name) => {
    const r = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("edge_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("edge_token");
    // Clear the last-good billing cache so a signed-out user can't sneak past
    // the paywall on their next login using stale access.
    try { localStorage.removeItem("edge_last_good_billing"); } catch { /* ignore */ }
    setUser(null);
    window.location.href = "/auth";
  };

  // Re-fetch the current user from /auth/me. Used after profile updates
  // (e.g. saving timezones on the Replay page) so downstream consumers of
  // `user` see the fresh values without a full page reload.
  const refreshUser = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
